'use strict';

const libPictProvider = require('pict-provider');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

const libModel = require('../data-import/ContractPackage-Model.js');
const libSchema = require('../data-import/DataImport-Schema.js');
const libLayouts = require('../data-import/ContractPackage-FieldLayouts.js');
const libTemplateData = require('../data-import/ContractPackage-Template-Data.js');
const libStrategyApply = require('pict-section-dataimport/source/services/DataImport-GuidStrategyApply.js');
const libComprehensionBuilder = require('pict-section-dataimport/source/services/DataImport-ComprehensionBuilder.js');
const libDelimitedParser = require('pict-section-dataimport/source/seams/DataImport-Parser-Delimited.js');
const libPictSectionUpload = require('pict-section-upload');
const libPictSectionAccordion = require('pict-section-accordion');

// The three source shapes the bespoke importer accepts.
const SOURCE_SHAPES =
{
	SHEET: 'sheet',   // "One file" — a giant denormalized sheet OR an Excel workbook (auto-detected on load)
	FILES: 'files',   // "Multiple files" — one file per level
};

const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'ContractPackageImportManager',
	AutoInitialize: true,
	AutoInitializeOrdinal: 91,
};

/**
 * ContractPackageImportManager — the orchestrator behind the bespoke "Import Contract Package" screen.
 *
 * Where the generic DataImportManager imports ONE flat entity, this imports the interconnected
 * Contract -> Project -> BidItem -> LineItem graph in one pass. It is a thin config-author over the SAME
 * engine + push the generic importer uses: it fetches the four live schemas, parses the chosen source
 * (reusing the wizard's parsers, Excel host-override included), turns the entity-aware UI choices into a
 * meadow-integration GUID strategy + multi-entity mapping via ContractPackage-Model, runs the shared
 * ComprehensionBuilder, then pushes the ordered comprehension through DataImportManager._pushViaHeadlight
 * (which already lands referenced-entities-first with GUID cross-linking). All render state lives at
 * AppData.ContractImportUI.
 */
class ContractPackageImportManagerProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _DEFAULT_CONFIGURATION, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._csvParser = null;
		this._schemaCache = null;      // { <entity>: mappedSchema }
		this._comprehension = null;    // last built comprehension (for the push step)
		this._pushContext = null;
		this._uploaders = {};          // slot -> created (pict-section-upload drop zone hash)
		this._uploaderInit = {};       // slot -> initialized once
		this._accHash = 'CPI-MapAccordion';   // the mapping accordion view hash
	}

	/** The shared Headlight rest client (schema reads + record upsert). */
	get restClient()
	{
		return (this.fable && this.fable.HeadlightRestClient)
			|| (this.pict && this.pict.fable && this.pict.fable.HeadlightRestClient)
			|| (this.pict && this.pict.HeadlightRestClient)
			|| null;
	}

	/** The generic import manager — we reuse its ordered multi-entity push + Excel parser. */
	get dataImportManager()
	{
		return this.pict.providers['DataImportManager'];
	}

	/** The app's shared modal (success / error toasts). */
	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	// ── Parsers (reused) ────────────────────────────────────────────────────────
	_xlsxParser()
	{
		// Reuse the generic manager's window.XLSX-aware host parser (has listSheetNames for the workbook shape).
		return this.dataImportManager ? this.dataImportManager._xlsxParser() : null;
	}

	_delimitedParser()
	{
		if (!this._csvParser) { this._csvParser = new libDelimitedParser(this.pict, { Kind: 'csv' }); }
		return this._csvParser;
	}

	/** Pick a parser for an uploaded file handle by extension (handle.Name from pict-section-upload). */
	_parserForFile(pHandle)
	{
		if (this._isExcel(pHandle)) { return this._xlsxParser(); }
		return this._delimitedParser(); // csv / tsv / txt
	}

	/** Is this handle an Excel file (by name)? */
	_isExcel(pHandle)
	{
		const tmpName = (pHandle && (pHandle.Name || pHandle.name)) ? String(pHandle.Name || pHandle.name).toLowerCase() : '';
		return /\.xlsx?$/.test(tmpName) || /\.xlsm$/.test(tmpName);
	}

	// ── Upload drop zones (pict-section-upload) ──────────────────────────────────
	// The generic import wizard embeds pict-section-upload to turn a browser File into a parser-ready
	// handle (getText/getArrayBuffer via FileReader). The bespoke view does the same, one drop zone per
	// upload slot ('sheet' for the giant/workbook shapes; one per created level for the file-per-level shape).

	/** Ensure the pict-section-upload provider is registered (the wizard registers it lazily too). */
	_ensureUploadProvider()
	{
		if (!this.pict.providers['Pict-Section-Upload'])
		{
			this.pict.addProvider('Pict-Section-Upload', libPictSectionUpload.default_configuration, libPictSectionUpload);
		}
		return this.pict.providers['Pict-Section-Upload'];
	}

	/** (Re)mount the drop zones for the current source shape into their view mounts (from onAfterRender). */
	mountUploaders()
	{
		const tmpState = this._ensureState();
		this._ensureUploadProvider();
		if (tmpState.IsSheet)
		{
			this._mountUploader('sheet', 'Drop your data file here, or');
		}
		if (tmpState.IsFiles)
		{
			(tmpState.CreatedLevels || []).forEach((pLevel) => this._mountUploader(pLevel.Entity, `Drop the ${pLevel.Label} file here, or`));
		}
	}

	/** Create (once) + render a drop zone for a slot, routing its handle to onFileReady. */
	_mountUploader(pSlot, pDropLabel)
	{
		const tmpMount = `#CPI-Upload-${pSlot}`;
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; } // mount not painted yet
		const tmpHash = `CPI-Upload-${pSlot}`;
		if (!this._uploaders[tmpHash])
		{
			this._ensureUploadProvider().createUploader(tmpHash,
				{
					DestinationAddress: tmpMount,
					Accept: '.csv,.tsv,.txt,.xlsx,.xls',
					DropLabel: pDropLabel,
					OnComplete: (pHandle) => this.onFileReady(pHandle, pSlot),
					OnRemove: () => this.onFileRemoved(pSlot),
				});
			this._uploaders[tmpHash] = true;
		}
		const tmpView = this.pict.views[tmpHash];
		if (tmpView)
		{
			if (!this._uploaderInit[tmpHash] && (typeof tmpView.initialize === 'function')) { tmpView.initialize(); this._uploaderInit[tmpHash] = true; }
			tmpView.render();
		}
	}

	// ── Mapping accordion (pict-section-accordion) ───────────────────────────────
	// Each created level is a collapsible panel (header = level + row count + key; body = the field grid),
	// so the map step shows one level's fields at a time instead of every box at once. Mirrors the wizard's
	// createAccordion + per-body-render + rebuild-on-wipe lifecycle.

	_ensureAccordionProvider()
	{
		if (!this.pict.providers['Pict-Section-Accordion'])
		{
			this.pict.addProvider('Pict-Section-Accordion', libPictSectionAccordion.default_configuration, libPictSectionAccordion);
		}
		return this.pict.providers['Pict-Section-Accordion'];
	}

	/** (Re)build the mapping accordion + render each level's field grid into its panel body (onAfterRender). */
	mountMappingAccordion()
	{
		const tmpState = this._ensureState();
		const tmpLevels = tmpState.CreatedLevels || [];
		const tmpMount = '#CPI-Map-Accordion';
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; } // mount not painted yet
		if (tmpLevels.length === 0) { return; }

		const tmpSteps = tmpLevels.map((pLevel) => (
			{
				Hash: pLevel.Entity,
				Title: pLevel.Label,
				Subtitle: `${pLevel.RowCount} row${pLevel.RowCount === 1 ? '' : 's'}${pLevel.KeyColumn ? ` · key: ${pLevel.KeyColumn}` : ''}`,
			}));

		this._ensureAccordionProvider().createAccordion(this._accHash,
			{
				DestinationAddress: tmpMount,
				RenderMode: 'accordion',
				AllowMultipleOpen: true,
				LinearGating: false,     // levels are independent — every panel is openable
				Steps: tmpSteps,
			});
		this.pict.views[this._accHash].render();

		// Render each level's body (Sheet picker + key + field grid) into its panel body container.
		tmpLevels.forEach((pLevel) =>
		{
			const tmpHTML = this.pict.parseTemplateByHash('CPI-Level-Body-Template', pLevel);
			this.pict.ContentAssignment.assignContent(`#PSA_Body_${this._accHash}_${pLevel.Entity}`, tmpHTML);
		});
	}

	// ── Pre-import Review tree ───────────────────────────────────────────────────
	// Renders the built comprehension as a browsable Contract→Project→BidItem→LineItem tree (items flow:
	// BidItem→LineItem) so the user can validate the actual records before pushing. Built as recursive HTML
	// into a mount (native <details> for expand/collapse), refreshed in onAfterRender like the accordion.

	mountReviewTree()
	{
		const tmpMount = '#CPI-Review-Tree';
		if (this.pict.ContentAssignment.getElement(tmpMount).length === 0) { return; }
		if (!this._comprehension) { return; }
		this.pict.ContentAssignment.assignContent(tmpMount, this._reviewTreeHTML());
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
	}

	/** Build the review tree HTML, rooted at the first created level in the push order. */
	_reviewTreeHTML()
	{
		const tmpComprehension = this._comprehension || {};
		const tmpOrder = (this._pushContext && this._pushContext.Order) || Object.keys(tmpComprehension);
		if (!tmpOrder.length) { return '<div class="cpi-tree-empty">Nothing to preview.</div>'; }
		const tmpRootGUIDs = Object.keys(tmpComprehension[tmpOrder[0]] || {});
		if (!tmpRootGUIDs.length) { return '<div class="cpi-tree-empty">No records were produced — check the key columns.</div>'; }
		return this._reviewNodeHTML(tmpOrder, 0, tmpRootGUIDs);
	}

	_reviewNodeHTML(pOrder, pLevelIdx, pGUIDs)
	{
		const tmpComprehension = this._comprehension || {};
		const tmpLevel = pOrder[pLevelIdx];
		const tmpRecords = tmpComprehension[tmpLevel] || {};
		const tmpMeta = libModel.findLevel(tmpLevel) || { Label: tmpLevel, Icon: 'fas fa-circle' };
		const tmpHasChild = (pLevelIdx + 1) < pOrder.length;
		const tmpChildLevel = tmpHasChild ? pOrder[pLevelIdx + 1] : null;
		const tmpChildRecords = tmpHasChild ? (tmpComprehension[tmpChildLevel] || {}) : {};
		const tmpChildMeta = tmpHasChild ? (libModel.findLevel(tmpChildLevel) || { Label: tmpChildLevel }) : null;

		return pGUIDs.map((pGUID) =>
		{
			const tmpRec = tmpRecords[pGUID] || {};
			const tmpTitle = this._esc(this._nodeTitle(tmpRec, pGUID));
			const tmpFields = this._nodeFieldsHTML(tmpRec);
			if (!tmpHasChild)
			{
				return `<div class="cpi-tree-leaf"><span class="cpi-tree-badge">${this._esc(tmpMeta.Label)}</span><span class="cpi-tree-title">${tmpTitle}</span>${tmpFields}</div>`;
			}
			const tmpChildGUIDs = Object.keys(tmpChildRecords).filter((pChild) => tmpChildRecords[pChild][`GUID${tmpLevel}`] === pGUID);
			const tmpSub = `${tmpChildGUIDs.length} ${tmpChildMeta.Label}${tmpChildGUIDs.length === 1 ? '' : 's'}`;
			const tmpOpen = (pLevelIdx < 1) ? ' open' : '';
			return `<details class="cpi-tree-node"${tmpOpen}><summary class="cpi-tree-head">`
				+ `<span class="cpi-tree-badge">${this._esc(tmpMeta.Label)}</span><span class="cpi-tree-title">${tmpTitle}</span>`
				+ `<span class="cpi-tree-sub">${this._esc(tmpSub)}</span></summary>${tmpFields}`
				+ `<div class="cpi-tree-children">${this._reviewNodeHTML(pOrder, pLevelIdx + 1, tmpChildGUIDs)}</div></details>`;
		}).join('');
	}

	/** A human title for a review node — the record's Name / number. */
	_nodeTitle(pRec, pGUID)
	{
		return pRec.Name || pRec.ItemNumber || pRec.ItemCode || pRec.Description || pGUID;
	}

	/** Up to six field chips (humanized label + value) for a node — mapped, non-internal fields only. */
	_nodeFieldsHTML(pRec)
	{
		const tmpChips = [];
		Object.keys(pRec).forEach((pKey) =>
		{
			if (tmpChips.length >= 6) { return; }
			if (/^GUID/.test(pKey) || /^_/.test(pKey) || (/^ID[A-Z]/.test(pKey)) || pKey === 'Name') { return; }
			const tmpValue = pRec[pKey];
			if (tmpValue == null || String(tmpValue) === '') { return; }
			tmpChips.push(`<span class="cpi-tree-chip"><span class="cpi-tree-chip-k">${this._esc(libLayouts.humanize(pKey))}</span> ${this._esc(tmpValue)}</span>`);
		});
		return tmpChips.length ? `<div class="cpi-tree-fields">${tmpChips.join('')}</div>` : '';
	}

	// ── State ───────────────────────────────────────────────────────────────────
	_ensureState()
	{
		if (!this.pict.AppData.ContractImportUI)
		{
			this.pict.AppData.ContractImportUI =
			{
				Mode: 'new',                 // 'new' (create all) | 'existing' (attach to a picked parent)
				SourceShape: SOURCE_SHAPES.SHEET,
				// Per-level scope: 'create' | 'pick'. 'new' mode forces all-create; 'existing' lets the user
				// pick a Contract (and optionally a Project) that become constant parents.
				Modes: { Contract: 'create', Project: 'create', BidItem: 'create', LineItem: 'create' },
				PickedIDs: {},
				PickedKeys: {},
				PickedLabels: {},
				// Detected columns for the loaded source (giant sheet: shared; per-entity: per level).
				Columns: [],
				SampleRows: [],
				PerEntityColumns: {},        // entity -> [columns] (workbook / files shapes)
				PerEntityRows: {},           // entity -> parsed rows (workbook / files shapes)
				RowsLoaded: 0,
				WorkbookTabs: [],            // a multi-tab workbook's sheet names (drives the per-level Sheet picker)
				TabAssignments: {},          // entity -> which workbook tab feeds it (best-guess, user-overridable)
				// Map step: entity -> { targetField: sourceColumn }, and entity -> keyColumn.
				FieldAssignments: {},
				KeyColumns: {},
				// Contract / Project pickers (add-to-existing).
				ContractOptions: [],
				ProjectOptions: [],
				// Items flow ('items') vs the full package flow ('package'): which columns match each row's
				// existing Contract + Project (per-row, many-project resolution).
				Flow: 'package',
				ContractKeyColumn: '',
				ProjectKeyColumn: '',
				// Derived render slots (rebuilt each prepare()).
				Preview: null,
				Warnings: [],
				LastResult: null,
				LastError: '',
				Busy: false,
			};
		}
		return this.pict.AppData.ContractImportUI;
	}

	/** Recompute derived render slots from core state. Called by the view's onBeforeRender. */
	prepare()
	{
		const tmpState = this._ensureState();

		// The levels that will be CREATED, in order, with their resolved key + column choices for the UI.
		const tmpPlan = this._plan();
		tmpState.CreatedLevels = tmpPlan.Levels
			.filter((pLevel) => pLevel.Mode === 'create')
			.map((pLevel) => (
				{
					Entity: pLevel.Entity,
					Label: pLevel.Label,
					Icon: pLevel.Icon,
					KeyColumn: (tmpState.KeyColumns[pLevel.Entity] || pLevel.KeyColumns[0] || ''),
					// The columns available to map/key this level (giant sheet shares one set).
					ColumnItems: this._columnsFor(pLevel.Entity).map((pColumn) => (
						{ Name: pColumn, Label: pColumn, Selected: ((tmpState.KeyColumns[pLevel.Entity] || pLevel.KeyColumns[0]) === pColumn) })),
					Groups: this._fieldGroupsFor(pLevel.Entity, tmpState),
					RowCount: (tmpState.PerEntityRows.__sheet)
						? tmpState.PerEntityRows.__sheet.length
						: (tmpState.PerEntityRows[pLevel.Entity] || []).length,
					// Files shape: each level gets its own file input inside its card.
					FileSlot: (tmpState.SourceShape === SOURCE_SHAPES.FILES) ? [ { Entity: pLevel.Entity, Label: pLevel.Label } ] : [],
					// Multi-tab workbook: a Sheet picker (all tabs + none), best-guess selected, user-overridable.
					TabSlot: (tmpState.WorkbookTabs && tmpState.WorkbookTabs.length)
						? [ { Entity: pLevel.Entity, TabItems: [ { Name: '', Label: '— none —', Selected: !tmpState.TabAssignments[pLevel.Entity] } ].concat(
							tmpState.WorkbookTabs.map((pTab) => ({ Name: pTab, Label: pTab, Selected: (tmpState.TabAssignments[pLevel.Entity] === pTab) }))) } ]
						: [],
				}));

		tmpState.IsItems = (tmpState.Flow === 'items');
		tmpState.IsPackage = !tmpState.IsItems;
		tmpState.Title = tmpState.IsItems ? 'Import Line Items' : 'Import Contract Package';
		tmpState.Subtitle = tmpState.IsItems
			? 'Add Bid Items and Line Items to existing Projects — each row matched to its Contract and Project by a column you choose, so one file can span many projects. Re-imports update in place.'
			: 'Load a Contract with its Projects, Bid Items and Line Items in one pass — from one file (a single sheet or an Excel workbook), or a file per level. Records interlink automatically, and re-importing the same data updates in place instead of duplicating.';
		tmpState.HeadIcon = tmpState.IsItems ? 'fas fa-list-check' : 'fas fa-folder-tree';
		tmpState.IsExisting = (tmpState.Mode === 'existing');
		tmpState.IsSheet = (tmpState.SourceShape !== SOURCE_SHAPES.FILES);   // "One file" — a sheet OR a workbook (auto-detected)
		tmpState.IsFiles = (tmpState.SourceShape === SOURCE_SHAPES.FILES);
		tmpState.HasColumns = (tmpState.Columns.length > 0) || (Object.keys(tmpState.PerEntityColumns).length > 0);

		// Segmented-control active classes + the top upload slot (the "One file" shape uploads once).
		tmpState.ModeNewClass = (tmpState.Mode === 'new') ? ' cpi-seg-on' : '';
		tmpState.ModeExistingClass = (tmpState.Mode === 'existing') ? ' cpi-seg-on' : '';
		tmpState.ShapeSheetClass = tmpState.IsSheet ? ' cpi-seg-on' : '';
		tmpState.ShapeFilesClass = tmpState.IsFiles ? ' cpi-seg-on' : '';
		tmpState.TopUploadSlot = tmpState.IsSheet
			? [ { Hint: 'CSV or Excel — a single sheet, or an Excel workbook with a tab per level' } ]
			: [];
		tmpState.MapSlot = (tmpState.HasColumns && tmpState.CreatedLevels.length > 0) ? [ {} ] : [];
		tmpState.CanImport = !!tmpState.Preview && !tmpState.Busy;
		tmpState.ReviewSlot = this._comprehension ? [ {} ] : [];   // the browsable record tree, after Preview

		tmpState.PreviewSlot = tmpState.Preview ? [ tmpState.Preview ] : [];
		tmpState.WarningSlot = (tmpState.Warnings || []).map((pWarning) => ({ Message: pWarning }));
		tmpState.ResultSlot = tmpState.LastResult ? [ tmpState.LastResult ] : [];
		tmpState.ErrorSlot = tmpState.LastError ? [ { Message: tmpState.LastError } ] : [];
		tmpState.PickerSlot = (tmpState.IsPackage && tmpState.IsExisting) ? [ {} ] : [];
		tmpState.ProjectPickerSlot = (tmpState.IsPackage && tmpState.IsExisting && tmpState.PickedIDs.Contract) ? [ {} ] : [];
		tmpState.NoteSlot = tmpState.SourceNote ? [ { Message: tmpState.SourceNote } ] : [];

		// Package flow shows the new/existing scope; items flow shows a "link to existing" intro + (once the
		// file is loaded) the Contract / Project column pickers used for per-row parent resolution.
		tmpState.PackageScopeSlot = tmpState.IsPackage ? [ {} ] : [];
		tmpState.ItemsIntroSlot = tmpState.IsItems ? [ {} ] : [];
		const tmpSheetColumns = (tmpState.Columns || []).map((pColumn) => (typeof pColumn === 'string') ? pColumn : (pColumn.SourceName || pColumn.Name));
		const fLinkItems = (pSelected) => [ { Name: '', Label: '— choose a column —', Selected: !pSelected } ].concat(
			tmpSheetColumns.map((pColumn) => ({ Name: pColumn, Label: pColumn, Selected: (pSelected === pColumn) })));
		tmpState.LinkSlot = (tmpState.IsItems && tmpState.HasColumns)
			? [ { ContractItems: fLinkItems(tmpState.ContractKeyColumn), ProjectItems: fLinkItems(tmpState.ProjectKeyColumn) } ]
			: [];

		return tmpState;
	}

	/** The normalized model plan for the current UI state. */
	_plan()
	{
		const tmpState = this._ensureState();
		const tmpModes = {};
		const tmpKeyColumns = {};
		Object.keys(tmpState.KeyColumns).forEach((pEntity) => { tmpKeyColumns[pEntity] = [ tmpState.KeyColumns[pEntity] ]; });

		if (tmpState.Flow === 'items')
		{
			// Import Bid Items + Line Items into EXISTING work: Contract + Project are referenced per-row by
			// the key column the user chose; only Bid Item + Line Item are created.
			libModel.PACKAGE_ENTITIES.forEach((pLevel) =>
			{
				tmpModes[pLevel.Entity] = (pLevel.Entity === 'Contract' || pLevel.Entity === 'Project') ? 'reference' : 'create';
			});
			return libModel.normalizePlan(
				{
					Modes: tmpModes,
					KeyColumns: tmpKeyColumns,
					ReferenceKeyColumns: { Contract: tmpState.ContractKeyColumn, Project: tmpState.ProjectKeyColumn },
				});
		}

		libModel.PACKAGE_ENTITIES.forEach((pLevel) =>
		{
			tmpModes[pLevel.Entity] = (tmpState.Mode === 'existing') ? (tmpState.Modes[pLevel.Entity] || 'create') : 'create';
		});
		return libModel.normalizePlan(
			{
				Modes: tmpModes,
				PickedIDs: tmpState.PickedIDs,
				PickedKeys: tmpState.PickedKeys,
				KeyColumns: tmpKeyColumns,
			});
	}

	/** The available source columns for a level (per-entity dataset if present, else the shared sheet). */
	_columnsFor(pEntity)
	{
		const tmpState = this._ensureState();
		if (tmpState.PerEntityColumns[pEntity] && tmpState.PerEntityColumns[pEntity].length)
		{
			return tmpState.PerEntityColumns[pEntity];
		}
		return (tmpState.Columns || []).map((pColumn) => (typeof pColumn === 'string') ? pColumn : pColumn.SourceName || pColumn.Name);
	}

	/** The mappable fields for a level (from the fetched schema), with the current assignment. */
	/**
	 * The mappable fields for a level, grouped + friendly-labeled like the record edit screen (Identity /
	 * Schedule / Financials / …), via ContractPackage-FieldLayouts. Each group's fields carry the source
	 * column dropdown. Ungrouped settable fields fall into an "Additional fields" section.
	 * @returns {Array<{Section:string, Fields:Array<object>}>}
	 */
	_fieldGroupsFor(pEntity, pState)
	{
		const tmpSchemaEntity = this._schemaEntity(pEntity);
		if (!tmpSchemaEntity) { return []; }
		const tmpAssigned = (pState.FieldAssignments[pEntity] || {});
		const tmpColumns = this._columnsFor(pEntity);
		const tmpLevel = libModel.findLevel(pEntity);
		// Foreign-key columns owned by the GUID strategy / constant-FK path are hidden from the data mapper.
		const tmpHiddenFK = {};
		(tmpLevel ? tmpLevel.Parents : []).forEach((pParent) => { tmpHiddenFK[(tmpLevel.ForeignKey[pParent] || '')] = true; });

		const tmpRequired = {};
		const tmpSettable = (tmpSchemaEntity.Fields || [])
			.filter((pField) => pField.Name !== tmpSchemaEntity.GUIDName && !tmpHiddenFK[pField.Name])
			.map((pField) => { tmpRequired[pField.Name] = !!pField.Required; return pField.Name; });

		const fColumnItems = (pFieldName) => [ { Name: '', Label: '— none —', Selected: !tmpAssigned[pFieldName] } ].concat(
			tmpColumns.map((pColumn) => ({ Name: pColumn, Label: pColumn, Selected: (tmpAssigned[pFieldName] === pColumn) })));

		return libLayouts.groupFields(pEntity, tmpSettable).map((pGroup) => (
			{
				Section: pGroup.Section,
				Fields: pGroup.Fields.map((pField) => (
					{
						Entity: pEntity,
						Name: pField.Key,
						Label: pField.Label,
						Required: !!tmpRequired[pField.Key],
						ColumnItems: fColumnItems(pField.Key),
					})),
			}));
	}

	_schemaEntity(pEntity)
	{
		return (this._schemaCache && this._schemaCache[pEntity]) || null;
	}

	// ── Wiring from the view ──────────────────────────────────────────────────────
	/** Switch flow: 'package' (create the whole Contract graph) vs 'items' (add Bid/Line Items to existing work). */
	setFlow(pFlow)
	{
		this.setFlowQuiet(pFlow);
		this._render();
	}

	/** Set the flow without rendering — for the router to call before it renders the view. */
	setFlowQuiet(pFlow)
	{
		const tmpState = this._ensureState();
		tmpState.Flow = (pFlow === 'items') ? 'items' : 'package';
		this._resetBuild();
		return this;
	}

	/** Items flow: which source column identifies each row's existing Contract / Project. */
	setContractKeyColumn(pColumn) { const tmpState = this._ensureState(); tmpState.ContractKeyColumn = pColumn || ''; this._resetBuild(); this._render(); }
	setProjectKeyColumn(pColumn) { const tmpState = this._ensureState(); tmpState.ProjectKeyColumn = pColumn || ''; this._resetBuild(); this._render(); }

	setMode(pMode)
	{
		const tmpState = this._ensureState();
		tmpState.Mode = (pMode === 'existing') ? 'existing' : 'new';
		if (tmpState.Mode === 'new')
		{
			tmpState.Modes = { Contract: 'create', Project: 'create', BidItem: 'create', LineItem: 'create' };
		}
		this._resetBuild();
		if (tmpState.Mode === 'existing' && tmpState.ContractOptions.length === 0)
		{
			this._loadContracts();
		}
		this._render();
	}

	setSourceShape(pShape)
	{
		const tmpState = this._ensureState();
		tmpState.SourceShape = pShape || SOURCE_SHAPES.SHEET;
		tmpState.Columns = []; tmpState.SampleRows = []; tmpState.PerEntityColumns = {}; tmpState.PerEntityRows = {}; tmpState.RowsLoaded = 0;
		tmpState.WorkbookTabs = []; tmpState.TabAssignments = {};
		tmpState.SourceNote = '';
		this._resetBuild();
		this._render();
	}

	/** The user picked an existing Contract to attach to → Contract becomes a constant parent. */
	pickContract(pIDContract, pLabel, pKey)
	{
		const tmpState = this._ensureState();
		const tmpID = parseInt(pIDContract, 10);
		// Picking (or clearing) a Contract resets any Project pick beneath it.
		this._clearProjectPick();
		tmpState.ProjectOptions = [];
		if (!tmpID)
		{
			delete tmpState.PickedIDs.Contract; delete tmpState.PickedKeys.Contract; delete tmpState.PickedLabels.Contract;
			tmpState.Modes.Contract = 'create';
		}
		else
		{
			tmpState.Modes.Contract = 'pick';
			tmpState.PickedIDs.Contract = tmpID;
			tmpState.PickedKeys.Contract = (pKey != null) ? String(pKey) : String(tmpID);
			tmpState.PickedLabels.Contract = pLabel || `Contract ${tmpID}`;
			// The optional "attach to a Project" picker self-loads (server-side search scoped to this contract) when
			// it mounts — no need to preload a capped ProjectOptions list here.
		}
		this._resetBuild();
		this._render();
	}

	/** The user picked an existing Project under the chosen Contract → import only Bid Items + Line Items. */
	pickProject(pIDProject, pLabel, pKey)
	{
		const tmpState = this._ensureState();
		const tmpID = parseInt(pIDProject, 10);
		if (!tmpID)
		{
			this._clearProjectPick();
		}
		else
		{
			tmpState.Modes.Project = 'pick';
			tmpState.PickedIDs.Project = tmpID;
			tmpState.PickedKeys.Project = (pKey != null) ? String(pKey) : String(tmpID);
			tmpState.PickedLabels.Project = pLabel || `Project ${tmpID}`;
		}
		this._resetBuild();
		this._render();
	}

	_clearProjectPick()
	{
		const tmpState = this._ensureState();
		delete tmpState.PickedIDs.Project; delete tmpState.PickedKeys.Project; delete tmpState.PickedLabels.Project;
		tmpState.Modes.Project = 'create';
	}

	// --- Searchable Contract / Project pickers (server-side search; replace the capped `/0/200` <select>s that made
	//     any contract/project past the cap unreachable for "attach to existing"). Follows the RoleClone pattern. ---

	_getJSONPromise(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_contractLabel(pC) { return `${(pC && pC.Name) || 'Contract'} (${(pC && (pC.PrimaryProjectNumber || pC.IDContract)) || ''})`; }
	_contractKey(pC) { return (pC && (pC.PrimaryProjectNumber || pC.Name)) || String((pC && pC.IDContract) || ''); }
	_projectLabel(pP) { return (pP && pP.Name) || `Project ${(pP && pP.IDProject) || ''}`; }
	_projectKey(pP) { return (pP && pP.Name) || String((pP && pP.IDProject) || ''); }

	/**
	 * Server-side entity search for a picker → { results:[{Value,Text,Record}], hasMore }. Parallel single-field LIKE
	 * queries merged + de-duped by id — NOT the picker's buildSearchFilter, whose parenthesized OR group is not honored
	 * by the Meadow server here (returns empty). Empty term → the requested page of the scope.
	 */
	_pickerSearch(pEntitySet, pScope, pSearchFields, pTerm, pIDField, pLabelFn, pPage)
	{
		const tmpPageSize = 25;
		const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
		const fToOption = (pRow) => ({ Value: Number(pRow[pIDField]), Text: pLabelFn(pRow), Record: pRow });
		const fMap = (pRows) => (Array.isArray(pRows) ? pRows : []).map(fToOption);
		if (!tmpTerm)
		{
			return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSONPromise(pURL),
				{ Set: pEntitySet, IDField: pIDField, Scope: pScope, SortField: pSearchFields[0], PageSize: tmpPageSize, ToOption: fToOption })('', pPage);
		}
		const tmpLike = encodeURIComponent(`%${tmpTerm}%`);
		return Promise.all(pSearchFields.map((pField) => this._getJSONPromise(`${pEntitySet}/FilteredTo/FBV~${pField}~LK~${tmpLike}~${pScope}/0/${tmpPageSize}`)))
			.then((pResults) =>
			{
				const tmpByID = {};
				pResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pRow) => { const tmpID = Number(pRow && pRow[pIDField]); if ((tmpID > 0) && !tmpByID[tmpID]) { tmpByID[tmpID] = pRow; } }));
				return { results: fMap(Object.keys(tmpByID).map((pKey) => tmpByID[pKey])).slice(0, 50), hasMore: false };
			});
	}

	/** (Re)mount the searchable Contract picker into #CPI-ContractPicker. Called from the view's onAfterRender. */
	mountContractPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#CPI-ContractPicker').length === 0) { return; }
		const tmpView = tmpPicker.createPicker('CPI-ContractPicker',
			{
				DestinationAddress: '#CPI-ContractPicker',
				ValueAddress: 'AppData.ContractImportUI.PickedIDs.Contract',
				Searchable: true, AllowClear: true, ClearLabel: '— choose a contract —',
				Placeholder: 'Search a contract by name or project number…',
				DataProvider: (pTerm, pPage) => this._pickerSearch('Contracts', 'FBV~Deleted~EQ~0', [ 'Name', 'PrimaryProjectNumber' ], pTerm, 'IDContract', (pC) => this._contractLabel(pC), pPage),
				ResolveValue: (pValue) =>
				{
					const tmpID = Number(pValue);
					if (!tmpID) { return Promise.resolve(null); }
					return this._getJSONPromise(`Contract/${tmpID}`).then((pC) => ((pC && pC.IDContract) ? { Value: Number(pC.IDContract), Text: this._contractLabel(pC) } : { Value: tmpID, Text: `#${tmpID}` }));
				},
				OnChange: (pValue, pOption) => { const tmpR = pOption && pOption.Record; this.pickContract(pValue || 0, tmpR ? this._contractLabel(tmpR) : '', tmpR ? this._contractKey(tmpR) : ''); },
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	/** (Re)mount the optional Project picker (scoped to the picked Contract) into #CPI-ProjectPicker. */
	mountProjectPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#CPI-ProjectPicker').length === 0) { return; }
		const tmpIDContract = this._ensureState().PickedIDs.Contract;
		if (!tmpIDContract) { return; }
		const tmpView = tmpPicker.createPicker('CPI-ProjectPicker',
			{
				DestinationAddress: '#CPI-ProjectPicker',
				ValueAddress: 'AppData.ContractImportUI.PickedIDs.Project',
				Searchable: true, AllowClear: true, ClearLabel: '— none (create Projects from the file) —',
				Placeholder: 'Search a project…',
				DataProvider: (pTerm, pPage) => this._pickerSearch('Projects', `FBV~IDContract~EQ~${tmpIDContract}`, [ 'Name' ], pTerm, 'IDProject', (pP) => this._projectLabel(pP), pPage),
				ResolveValue: (pValue) =>
				{
					const tmpID = Number(pValue);
					if (!tmpID) { return Promise.resolve(null); }
					return this._getJSONPromise(`Project/${tmpID}`).then((pP) => ((pP && pP.IDProject) ? { Value: Number(pP.IDProject), Text: this._projectLabel(pP) } : { Value: tmpID, Text: `#${tmpID}` }));
				},
				OnChange: (pValue, pOption) => { const tmpR = pOption && pOption.Record; this.pickProject(pValue || 0, tmpR ? this._projectLabel(tmpR) : '', tmpR ? this._projectKey(tmpR) : ''); },
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	/** The map step: assign a source column to a target field (or clear it with ''). */
	assignField(pEntity, pField, pColumn)
	{
		const tmpState = this._ensureState();
		if (!tmpState.FieldAssignments[pEntity]) { tmpState.FieldAssignments[pEntity] = {}; }
		if (pColumn) { tmpState.FieldAssignments[pEntity][pField] = pColumn; }
		else { delete tmpState.FieldAssignments[pEntity][pField]; }
		this._resetBuild();
	}

	/** The map step: choose the natural-key column that composes a level's GUID (idempotency anchor). */
	setKeyColumn(pEntity, pColumn)
	{
		const tmpState = this._ensureState();
		tmpState.KeyColumns[pEntity] = pColumn;
		this._resetBuild();
		this._render();
	}

	_resetBuild()
	{
		const tmpState = this._ensureState();
		tmpState.Preview = null; tmpState.LastResult = null; tmpState.LastError = ''; this._comprehension = null; this._pushContext = null;
	}

	// ── Schema + source loading ───────────────────────────────────────────────────
	/** Fetch the four live schemas once (parallel), map to the dataimport shape, cache. */
	ensureSchemas()
	{
		if (this._schemaCache) { return Promise.resolve(this._schemaCache); }
		const tmpRestClient = this.restClient;
		if (!tmpRestClient) { return Promise.reject(new Error('HeadlightRestClient is not available for the schema read.')); }
		return Promise.all(libModel.ENTITY_ORDER.map((pEntity) => new Promise((resolve, reject) =>
		{
			tmpRestClient.getJSON(`${pEntity}/Schema`, (pError, pBody) =>
			{
				if (pError) { return reject(pError); }
				const tmpMapped = libSchema.mapEntitySchema(pEntity, pBody);
				resolve(tmpMapped.Entities[0]);
			});
		}))).then((pEntities) =>
		{
			this._schemaCache = {};
			pEntities.forEach((pEntity) => { this._schemaCache[pEntity.Entity] = pEntity; });
			return this._schemaCache;
		});
	}

	/** Load the Contract picker options (active contracts) for the add-to-existing mode. */
	_loadContracts()
	{
		const tmpRestClient = this.restClient;
		if (!tmpRestClient) { return; }
		// Lite list of active contracts; Name + PrimaryProjectNumber label the option, GUID/key anchors the GUID.
		tmpRestClient.getJSON('Contracts/FilteredTo/FBV~Deleted~EQ~0/0/200', (pError, pBody) =>
		{
			const tmpState = this._ensureState();
			if (pError || !Array.isArray(pBody)) { tmpState.ContractOptions = []; this._render(); return; }
			tmpState.ContractOptions = pBody.map((pContract) => (
				{
					IDContract: pContract.IDContract,
					Key: pContract.PrimaryProjectNumber || pContract.Name || String(pContract.IDContract),
					Label: `${pContract.Name || 'Contract'} (${pContract.PrimaryProjectNumber || pContract.IDContract})`,
				}));
			this._render();
		}, 0, 200);
	}

	/** Load the Project picker options for the chosen Contract (optional — pick to import only below Project). */
	_loadProjects(pIDContract)
	{
		const tmpRestClient = this.restClient;
		const tmpState = this._ensureState();
		if (!tmpRestClient || !pIDContract) { tmpState.ProjectOptions = []; return; }
		tmpRestClient.getJSON(`Projects/FilteredTo/FBV~IDContract~EQ~${pIDContract}/0/200`, (pError, pBody) =>
		{
			if (pError || !Array.isArray(pBody)) { tmpState.ProjectOptions = []; this._render(); return; }
			tmpState.ProjectOptions = pBody.map((pProject) => (
				{
					IDProject: pProject.IDProject,
					Key: pProject.Name || String(pProject.IDProject),
					Label: pProject.Name || `Project ${pProject.IDProject}`,
				}));
			this._render();
		}, 0, 200);
	}

	/**
	 * A parser-ready file handle arrived from a pict-section-upload drop zone (getText/getArrayBuffer). Parse
	 * it into the right slot for the current source shape. Slot is 'sheet' (giant / workbook) or an entity.
	 * @param {Record<string,any>} pHandle @param {string} pSlot
	 */
	onFileReady(pHandle, pSlot)
	{
		if (!pHandle) { return; }
		const tmpState = this._ensureState();
		tmpState.LastError = '';

		this.ensureSchemas().then(() =>
		{
			if (tmpState.SourceShape === SOURCE_SHAPES.FILES) { return this._loadEntityFile(pSlot, pHandle); }
			// sheet / workbook: adapt to the file — any Excel auto-detects single-sheet vs a tab-per-level.
			if (this._isExcel(pHandle)) { return this._loadWorkbook(pHandle); }
			return this._loadSheet(pHandle); // a CSV in the single-file shapes is one giant sheet
		}).then(() => { this._render(); }).catch((pError) => this._onError(pError));
	}

	/** A file was removed from a drop zone — drop that slot's parsed data. */
	onFileRemoved(pSlot)
	{
		const tmpState = this._ensureState();
		if (pSlot === 'sheet') { tmpState.Columns = []; tmpState.SampleRows = []; delete tmpState.PerEntityRows.__sheet; }
		else { delete tmpState.PerEntityColumns[pSlot]; delete tmpState.PerEntityRows[pSlot]; }
		tmpState.RowsLoaded = Object.keys(tmpState.PerEntityRows).reduce((pSum, pKey) => pSum + (tmpState.PerEntityRows[pKey] || []).length, 0);
		this._resetBuild();
		this._render();
	}

	/** Giant sheet: parse one file into a shared rows + columns set that feeds all four levels. */
	_loadSheet(pFile)
	{
		const tmpParser = this._parserForFile(pFile);
		const tmpState = this._ensureState();
		return Promise.all([ tmpParser.detect(pFile, {}), tmpParser.parse(pFile, {}) ]).then((pResults) =>
		{
			tmpState.PerEntityColumns = {};   // a giant sheet shares ONE column set across all levels
			tmpState.WorkbookTabs = []; tmpState.TabAssignments = {};   // not a per-tab workbook
			tmpState.Columns = (pResults[0].Columns || []).map((pColumn) => pColumn.SourceName || pColumn.Name || pColumn);
			tmpState.SampleRows = pResults[0].SampleRows || [];
			tmpState.PerEntityRows = { __sheet: pResults[1] || [] };
			tmpState.RowsLoaded = (pResults[1] || []).length;
			tmpState.SourceNote = `Loaded a single sheet — ${(pResults[1] || []).length} rows feed all four levels.`;
			this._seedDefaults();
		});
	}

	/**
	 * Excel: adapt to the file. A single-sheet workbook is a giant denormalized sheet (→ _loadSheet). A
	 * multi-tab workbook is per-level: best-guess each level's tab by name, parse the guessed tabs, and keep
	 * the tab list + handle so the user can reassign any level's tab in the map step (re-parsing on change).
	 */
	_loadWorkbook(pFile)
	{
		const tmpXlsx = this._xlsxParser();
		const tmpState = this._ensureState();
		if (!tmpXlsx || (typeof tmpXlsx.listSheetNames !== 'function')) { return this._loadSheet(pFile); }
		return tmpXlsx.listSheetNames(pFile).then((pSheetNames) =>
		{
			const tmpTabs = (pSheetNames || []).filter((pTab) => !!pTab);
			if (tmpTabs.length <= 1) { return this._loadSheet(pFile); }   // a single sheet is a giant sheet

			// Multi-tab: per-level. Keep the handle + tabs so the user can reassign; best-guess each level.
			this._workbookHandle = pFile;
			tmpState.WorkbookTabs = tmpTabs;
			tmpState.Columns = []; tmpState.SampleRows = []; delete tmpState.PerEntityRows.__sheet;
			tmpState.PerEntityColumns = {}; tmpState.PerEntityRows = {}; tmpState.TabAssignments = {};
			libModel.ENTITY_ORDER.forEach((pEntity) => { tmpState.TabAssignments[pEntity] = this._matchTab(pEntity, tmpTabs); });

			return Promise.all(libModel.ENTITY_ORDER
				.filter((pEntity) => tmpState.TabAssignments[pEntity])
				.map((pEntity) => this._parseTabInto(pEntity, tmpState.TabAssignments[pEntity])))
				.then(() =>
				{
					tmpState.RowsLoaded = Object.keys(tmpState.PerEntityRows).reduce((pSum, pKey) => pSum + (tmpState.PerEntityRows[pKey] || []).length, 0);
					const tmpMatched = libModel.ENTITY_ORDER.filter((pE) => tmpState.TabAssignments[pE]).length;
					tmpState.SourceNote = `Multi-tab workbook — ${tmpTabs.length} tabs, ${tmpMatched} auto-matched to levels. Set each level's sheet below.`;
					this._seedDefaults();
				});
		});
	}

	/** Parse a specific workbook tab into a level's dataset (on load + when the user reassigns a tab). */
	_parseTabInto(pEntity, pTab)
	{
		const tmpXlsx = this._xlsxParser();
		const tmpState = this._ensureState();
		return Promise.all([ tmpXlsx.detect(this._workbookHandle, { Xlsx: { SheetName: pTab } }), tmpXlsx.parse(this._workbookHandle, { Xlsx: { SheetName: pTab } }) ]).then((pResults) =>
		{
			tmpState.PerEntityColumns[pEntity] = (pResults[0].Columns || []).map((pColumn) => pColumn.SourceName || pColumn.Name || pColumn);
			tmpState.PerEntityRows[pEntity] = pResults[1] || [];
		});
	}

	/** Reassign which workbook tab feeds a level (multi-tab workbook), re-parsing that tab. */
	setTab(pEntity, pTab)
	{
		const tmpState = this._ensureState();
		if (!tmpState.WorkbookTabs || !this._workbookHandle) { return; }
		tmpState.TabAssignments[pEntity] = pTab || '';
		// The tab's columns change, so this level's seeded key + field mappings are stale — reseed them.
		delete tmpState.KeyColumns[pEntity];
		delete tmpState.FieldAssignments[pEntity];
		this._resetBuild();
		const fDone = () =>
		{
			tmpState.RowsLoaded = Object.keys(tmpState.PerEntityRows).reduce((pSum, pKey) => pSum + (tmpState.PerEntityRows[pKey] || []).length, 0);
			this._seedDefaults();
			this._render();
		};
		if (pTab) { this._parseTabInto(pEntity, pTab).then(fDone).catch((pError) => this._onError(pError)); }
		else { delete tmpState.PerEntityColumns[pEntity]; delete tmpState.PerEntityRows[pEntity]; fDone(); }
	}

	/** Files shape: parse one file into a single level's dataset. */
	_loadEntityFile(pEntity, pFile)
	{
		const tmpParser = this._parserForFile(pFile);
		const tmpState = this._ensureState();
		return Promise.all([ tmpParser.detect(pFile, {}), tmpParser.parse(pFile, {}) ]).then((pResults) =>
		{
			tmpState.PerEntityColumns[pEntity] = (pResults[0].Columns || []).map((pColumn) => pColumn.SourceName || pColumn.Name || pColumn);
			tmpState.PerEntityRows[pEntity] = pResults[1] || [];
			tmpState.RowsLoaded = Object.keys(tmpState.PerEntityRows).reduce((pSum, pKey) => pSum + (tmpState.PerEntityRows[pKey] || []).length, 0);
			this._seedDefaults();
		});
	}

	/** Fuzzy-match a workbook tab name to an entity (Contract/Project/BidItem/LineItem). */
	_matchTab(pEntity, pTabs)
	{
		const tmpLower = String(pEntity).toLowerCase();
		const tmpAlt = tmpLower.replace('item', ' item');
		return (pTabs || []).find((pTab) =>
		{
			const tmpTab = String(pTab).toLowerCase().replace(/[^a-z]/g, '');
			return (tmpTab.indexOf(tmpLower) > -1) || (tmpTab.indexOf(tmpAlt.replace(/\s/g, '')) > -1);
		}) || '';
	}

	/** After a source loads, seed key columns + obvious field mappings by name match (user can override). */
	_seedDefaults()
	{
		const tmpState = this._ensureState();
		libModel.PACKAGE_ENTITIES.forEach((pLevel) =>
		{
			const tmpColumns = this._columnsFor(pLevel.Entity);
			if (!tmpState.KeyColumns[pLevel.Entity])
			{
				// Default key column: an exact DefaultKeyColumns match, else a fuzzy KeyHints match against
				// realistic headers ("Contract Number", "Project Name", …), else the first column.
				const tmpNorm = (pName) => String(pName).toLowerCase().replace(/[^a-z0-9]/g, '');
				let tmpKey = pLevel.DefaultKeyColumns.find((pColumn) => tmpColumns.indexOf(pColumn) > -1);
				if (!tmpKey)
				{
					(pLevel.KeyHints || []).some((pHint) =>
					{
						const tmpMatch = tmpColumns.find((pColumn) => tmpNorm(pColumn).indexOf(pHint) > -1);
						if (tmpMatch) { tmpKey = tmpMatch; return true; }
						return false;
					});
				}
				tmpKey = tmpKey || tmpColumns[0] || '';
				if (tmpKey) { tmpState.KeyColumns[pLevel.Entity] = tmpKey; }
			}
			// Auto-map source columns to fields: exact schema name, OR the friendly label ("Cost / Unit" ->
			// CostPerUnit) so a template's human headers land pre-mapped.
			const tmpSchemaEntity = this._schemaEntity(pLevel.Entity);
			if (tmpSchemaEntity)
			{
				if (!tmpState.FieldAssignments[pLevel.Entity]) { tmpState.FieldAssignments[pLevel.Entity] = {}; }
				const tmpFieldNames = {};
				(tmpSchemaEntity.Fields || []).forEach((pField) => { tmpFieldNames[pField.Name] = true; });
				tmpColumns.forEach((pColumn) =>
				{
					const tmpFieldKey = tmpFieldNames[pColumn] ? pColumn : libLayouts.keyForHeader(pLevel.Entity, pColumn);
					if (tmpFieldKey && tmpFieldNames[tmpFieldKey] && !tmpState.FieldAssignments[pLevel.Entity][tmpFieldKey])
					{
						tmpState.FieldAssignments[pLevel.Entity][tmpFieldKey] = pColumn;
					}
				});
			}
		});

		// Items flow: best-guess which column identifies each row's existing Contract / Project.
		if (tmpState.Flow === 'items')
		{
			const tmpColumns = this._columnsFor('BidItem');
			const tmpNorm = (pName) => String(pName).toLowerCase().replace(/[^a-z0-9]/g, '');
			const fSeed = (pCurrent, pHints) =>
			{
				if (pCurrent) { return pCurrent; }
				let tmpMatch = '';
				pHints.some((pHint) => { const tmpCol = tmpColumns.find((pColumn) => tmpNorm(pColumn).indexOf(pHint) > -1); if (tmpCol) { tmpMatch = tmpCol; return true; } return false; });
				return tmpMatch;
			};
			tmpState.ContractKeyColumn = fSeed(tmpState.ContractKeyColumn, [ 'contractnumber', 'contractno', 'contractid', 'contract' ]);
			tmpState.ProjectKeyColumn = fSeed(tmpState.ProjectKeyColumn, [ 'projectname', 'projectnumber', 'projectid', 'project' ]);
		}
	}

	// ── Build + push ──────────────────────────────────────────────────────────────
	/** Build the comprehension from the current plan + source, computing per-level counts + validation. */
	buildPreview()
	{
		const tmpState = this._ensureState();
		if (tmpState.Flow === 'items') { return this._buildItemsPreview(); }
		try { this._composeAndPreview(this._plan(), null); }
		catch (pError) { tmpState.Preview = null; tmpState.LastError = (pError && pError.message) ? pError.message : String(pError); this._render(); }
	}

	/** Items flow: resolve each row's existing Contract + Project by key column, then build from resolved rows. */
	_buildItemsPreview()
	{
		const tmpState = this._ensureState();
		if (!tmpState.ContractKeyColumn || !tmpState.ProjectKeyColumn)
		{
			tmpState.Preview = null; tmpState.LastError = 'Choose which columns identify the Contract and Project first.'; this._render(); return;
		}
		const tmpRows = tmpState.PerEntityRows.__sheet || [];
		if (tmpRows.length === 0) { tmpState.Preview = null; tmpState.LastError = 'Upload a file with Bid Item / Line Item rows first.'; this._render(); return; }
		tmpState.Busy = true; tmpState.LastError = ''; this._render();
		this._resolveParents(tmpRows).then((pResolution) =>
		{
			tmpState.Busy = false;
			try
			{
				this._composeAndPreview(this._plan(), pResolution.ResolvedRows);
				if (pResolution.UnresolvedCount > 0)
				{
					tmpState.Warnings = (tmpState.Warnings || []).concat(
						`${pResolution.UnresolvedCount} row(s) skipped — no matching Contract/Project${pResolution.Samples.length ? ` (e.g. ${pResolution.Samples.join('; ')})` : ''}.`);
					this._render();
				}
			}
			catch (pError) { tmpState.Preview = null; tmpState.LastError = (pError && pError.message) ? pError.message : String(pError); this._render(); }
		}).catch((pError) => { tmpState.Busy = false; this._onError(pError); });
	}

	/** Compose strategy + mapping + comprehension for a plan; optionally from explicit sheet rows (items flow). */
	_composeAndPreview(pPlan, pSheetRowsOverride)
	{
		const tmpState = this._ensureState();
		if (pPlan.Order.length === 0) { throw new Error('No levels selected to create.'); }

		const tmpStrategyConfig = libModel.buildStrategyConfig(pPlan);
		const tmpMapping = libModel.buildMapping(pPlan, tmpState.FieldAssignments);
		const tmpSchema = { Order: libModel.ENTITY_ORDER, Entities: libModel.ENTITY_ORDER.map((pEntity) => this._schemaEntity(pEntity)).filter(Boolean) };
		libStrategyApply.applyStrategy(tmpMapping, tmpStrategyConfig, libModel.contextCatalog(), tmpSchema);

		const tmpBuilder = new libComprehensionBuilder(this.pict);
		const tmpComprehension = {};
		const tmpReport = { EntityCounts: {}, BadRecords: [] };

		// One shared dataset (giant / single-sheet workbook / items-flow resolved rows) vs per-entity datasets.
		if (pSheetRowsOverride || tmpState.PerEntityRows.__sheet)
		{
			const tmpRows = libModel.injectConstantColumns(pSheetRowsOverride || tmpState.PerEntityRows.__sheet || [], pPlan);
			const tmpResult = tmpBuilder.build(tmpMapping, tmpRows);
			Object.assign(tmpComprehension, tmpResult.Comprehension);
			Object.assign(tmpReport.EntityCounts, tmpResult.Report.EntityCounts);
			tmpReport.BadRecords = tmpResult.Report.BadRecords;
		}
		else
		{
			pPlan.Order.forEach((pEntity) =>
			{
				const tmpEntityRows = libModel.injectConstantColumns(tmpState.PerEntityRows[pEntity] || [], pPlan);
				const tmpMini = { Order: [ pEntity ], Entities: {} };
				tmpMini.Entities[pEntity] = tmpMapping.Entities[pEntity];
				const tmpResult = tmpBuilder.build(tmpMini, tmpEntityRows);
				tmpComprehension[pEntity] = tmpResult.Comprehension[pEntity] || {};
				tmpReport.EntityCounts[pEntity] = Object.keys(tmpComprehension[pEntity]).length;
				tmpReport.BadRecords = tmpReport.BadRecords.concat(tmpResult.Report.BadRecords || []);
			});
		}

		const tmpSizes = {};
		libModel.ENTITY_ORDER.forEach((pEntity) =>
		{
			const tmpGUIDField = (this._schemaEntity(pEntity) && (this._schemaEntity(pEntity).Fields || []).find((pField) => pField.IsGUID));
			if (tmpGUIDField && tmpGUIDField.Size) { tmpSizes[pEntity] = tmpGUIDField.Size; }
		});
		this._comprehension = tmpComprehension;
		this._pushContext = libModel.buildPushContext(pPlan, tmpSizes);

		tmpState.Warnings = this._collectWarnings(pPlan, tmpMapping, tmpReport);
		tmpState.Preview =
		{
			Counts: pPlan.Order.map((pEntity) => (
				{ Entity: pEntity, Label: libModel.findLevel(pEntity).Label, Count: (tmpReport.EntityCounts[pEntity] || 0) })),
			BadCount: (tmpReport.BadRecords || []).length,
			Summary: pPlan.Order.map((pEntity) => `${tmpReport.EntityCounts[pEntity] || 0} ${libModel.findLevel(pEntity).Label}${(tmpReport.EntityCounts[pEntity] === 1) ? '' : 's'}`).join(' · '),
		};
		tmpState.LastError = '';
		this._render();
	}

	/**
	 * Resolve each row's existing Contract + Project by the chosen key columns, injecting `__PKG_IDContract`
	 * / `__PKG_IDProject`. Rows whose Contract or Project don't match are dropped (and counted).
	 * @param {Array<Record<string,any>>} pRows
	 * @returns {Promise<{ResolvedRows:Array<object>, UnresolvedCount:number, Samples:Array<string>}>}
	 */
	_resolveParents(pRows)
	{
		const tmpState = this._ensureState();
		const tmpContractCol = tmpState.ContractKeyColumn;
		const tmpProjectCol = tmpState.ProjectKeyColumn;
		const tmpKey = (pVal) => String(pVal == null ? '' : pVal).trim().toLowerCase();

		return this._fetchContractsMap().then((pContractMap) =>
		{
			// Which contracts are actually referenced → fetch just those contracts' projects.
			const tmpNeeded = {};
			pRows.forEach((pRow) => { const tmpID = pContractMap[tmpKey(pRow[tmpContractCol])]; if (tmpID) { tmpNeeded[tmpID] = true; } });
			return this._fetchProjectsMap(Object.keys(tmpNeeded)).then((pProjectMap) =>
			{
				let tmpUnresolved = 0;
				const tmpSamples = [];
				const tmpResolved = [];
				pRows.forEach((pRow) =>
				{
					const tmpIDContract = pContractMap[tmpKey(pRow[tmpContractCol])];
					const tmpIDProject = tmpIDContract ? pProjectMap[`${tmpIDContract}|${tmpKey(pRow[tmpProjectCol])}`] : undefined;
					if (!tmpIDContract || !tmpIDProject)
					{
						tmpUnresolved++;
						if (tmpSamples.length < 4) { tmpSamples.push(`${pRow[tmpContractCol] || '?'} / ${pRow[tmpProjectCol] || '?'}`); }
						return;
					}
					const tmpRow = Object.assign({}, pRow);
					tmpRow[libModel.resolvedIDColumn('Contract')] = String(tmpIDContract);
					tmpRow[libModel.resolvedIDColumn('Project')] = String(tmpIDProject);
					tmpResolved.push(tmpRow);
				});
				return { ResolvedRows: tmpResolved, UnresolvedCount: tmpUnresolved, Samples: tmpSamples };
			});
		});
	}

	/**
	 * Read an ENTIRE filtered set by paging to exhaustion (begin/count until a short page). Row-resolution maps must be
	 * complete or import rows silently vanish — the old single `/0/2000` (contracts) and `/0/500` (projects) fetches
	 * dropped every row that referenced a parent past the cap. A one-time import can afford the extra pages.
	 */
	_fetchAllRows(pEntitySet, pFilter)
	{
		const tmpRestClient = this.restClient;
		const tmpPageSize = 2000;
		return new Promise((resolve) =>
		{
			if (!tmpRestClient) { return resolve([]); }
			const tmpAll = [];
			const fPage = (pBegin) =>
			{
				tmpRestClient.getJSON(`${pEntitySet}/FilteredTo/${pFilter}/${pBegin}/${tmpPageSize}`, (pError, pBody) =>
				{
					if (pError || !Array.isArray(pBody) || (pBody.length === 0)) { return resolve(tmpAll); }
					tmpAll.push(...pBody);
					if (pBody.length < tmpPageSize) { return resolve(tmpAll); }   // last (short) page
					fPage(pBegin + tmpPageSize);
				}, pBegin, tmpPageSize);
			};
			fPage(0);
		});
	}

	/** Fetch active contracts → { <natural key lowercased>: IDContract } (matched on any project number or name). */
	_fetchContractsMap()
	{
		return this._fetchAllRows('Contracts', 'FBV~Deleted~EQ~0').then((pRows) =>
		{
			const tmpMap = {};
			pRows.forEach((pContract) =>
			{
				[ pContract.PrimaryProjectNumber, pContract.CountyProjectNumber, pContract.StateProjectNumber, pContract.FederalProjectNumber, pContract.ExternalContractID, pContract.Name ]
					.forEach((pK) => { if (pK != null && String(pK).trim() !== '') { tmpMap[String(pK).trim().toLowerCase()] = pContract.IDContract; } });
			});
			return tmpMap;
		});
	}

	/** Fetch projects for the given contracts → { `<IDContract>|<name lowercased>`: IDProject }. */
	_fetchProjectsMap(pContractIDs)
	{
		const tmpMap = {};
		if (!this.restClient || !pContractIDs.length) { return Promise.resolve(tmpMap); }
		return pContractIDs.reduce((pPromise, pIDContract) => pPromise.then(() => this._fetchAllRows('Projects', `FBV~IDContract~EQ~${pIDContract}`).then((pRows) =>
		{
			pRows.forEach((pProject) =>
			{
				[ pProject.Name, pProject.ExternalProjectID, pProject.PurchaseOrderNumber ]
					.forEach((pK) => { if (pK != null && String(pK).trim() !== '') { tmpMap[`${pIDContract}|${String(pK).trim().toLowerCase()}`] = pProject.IDProject; } });
			});
		})), Promise.resolve()).then(() => tmpMap);
	}

	/** Gather human-readable warnings (missing keys, empty levels, unmapped required fields). */
	_collectWarnings(pPlan, pMapping, pReport)
	{
		const tmpState = this._ensureState();
		const tmpWarnings = [];
		pPlan.Order.forEach((pEntity) =>
		{
			if (!tmpState.KeyColumns[pEntity]) { tmpWarnings.push(`${libModel.findLevel(pEntity).Label} has no key column — re-imports may duplicate.`); }
			if ((pReport.EntityCounts[pEntity] || 0) === 0) { tmpWarnings.push(`No ${libModel.findLevel(pEntity).Label} records were produced — check the source column that keys it.`); }
		});
		if ((pReport.BadRecords || []).length > 0) { tmpWarnings.push(`${pReport.BadRecords.length} row(s) produced no valid GUID and were skipped.`); }
		return tmpWarnings;
	}

	/** Push the built comprehension via the shared ordered multi-entity push. */
	runImport()
	{
		const tmpState = this._ensureState();
		if (!this._comprehension || !this._pushContext)
		{
			this.buildPreview();
			// The items-flow preview is async (parent resolution), so it isn't ready yet — the user reviews
			// the resolved counts + skipped rows, then clicks Import again.
			if (tmpState.Flow === 'items' || !this._comprehension) { return; }
		}
		if (!this._comprehension || tmpState.LastError) { return; }
		const tmpManager = this.dataImportManager;
		if (!tmpManager || (typeof tmpManager._pushViaHeadlight !== 'function'))
		{
			return this._onError(new Error('The import push seam is unavailable.'));
		}
		tmpState.Busy = true; tmpState.LastResult = null; tmpState.LastError = ''; this._render();

		tmpManager._pushViaHeadlight(this._comprehension, this._pushContext).then((pResult) =>
		{
			tmpState.Busy = false;
			const tmpIsItems = (tmpState.Flow === 'items');
			const tmpMessage = (pResult && pResult.Message) ? pResult.Message : (tmpIsItems ? 'Line items imported.' : 'Contract package imported.');
			tmpState.LastResult = tmpIsItems
				? { Message: tmpMessage, ViewRoute: '#/PSRS/LineItem/List', ViewLabel: 'View line items' }
				: { Message: tmpMessage, ViewRoute: '#/PSRS/Contract/List', ViewLabel: 'View contracts' };
			if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(tmpMessage, { type: 'success' }); }
			this._render();
		}).catch((pError) => { tmpState.Busy = false; this._onError(pError); });
	}

	_onError(pError)
	{
		const tmpState = this._ensureState();
		const tmpMessage = (pError && pError.message) ? pError.message : String(pError || 'Import failed.');
		tmpState.LastError = tmpMessage;
		if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(tmpMessage, { type: 'error', duration: 6000 }); }
		this._render();
	}

	_render()
	{
		if (this.pict.views['ContractPackageImport']) { this.pict.views['ContractPackageImport'].render(); }
	}

	/**
	 * Download the styled Contract Package Excel template (Read Me + a tab per level). The bytes are
	 * embedded in the bundle (data-import/ContractPackage-Template-Data.js) and handed to the browser via a
	 * Blob, so the template ships INSIDE the app and downloads with zero dependency on the server serving
	 * /assets/ — which is why the old `<a href="assets/…">` link was showing as "unavailable". Invoked from
	 * the step-1 scope card link. Uses the standard client-side Blob-download idiom.
	 */
	downloadTemplate()
	{
		if ((typeof document === 'undefined') || (typeof Blob === 'undefined') || (typeof URL === 'undefined'))
		{
			return;
		}
		try
		{
			// base64 → bytes (atob in the browser; Buffer only as a non-browser safety net for tests).
			const tmpBinary = (typeof atob === 'function')
				? atob(libTemplateData.Base64)
				: Buffer.from(libTemplateData.Base64, 'base64').toString('binary');
			const tmpBytes = new Uint8Array(tmpBinary.length);
			for (let i = 0; i < tmpBinary.length; i++) { tmpBytes[i] = tmpBinary.charCodeAt(i); }

			const tmpBlob = new Blob([ tmpBytes ], { type: libTemplateData.MimeType });
			const tmpURL = URL.createObjectURL(tmpBlob);
			const tmpLink = document.createElement('a');
			tmpLink.href = tmpURL;
			tmpLink.download = libTemplateData.FileName;
			document.body.appendChild(tmpLink);
			tmpLink.click();
			document.body.removeChild(tmpLink);
			URL.revokeObjectURL(tmpURL);
		}
		catch (pError)
		{
			if (this.pict && this.pict.log) { this.pict.log.error(`Contract Package template download failed: ${pError.message || pError}`); }
			if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast('Could not open the Excel template.', { type: 'error' }); }
		}
	}
}

module.exports = ContractPackageImportManagerProvider;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
module.exports.SOURCE_SHAPES = SOURCE_SHAPES;
